<!-- src/views/BoozelogView.vue -->
<template>
  <div class="boozelog page">
    <section class="hero glass-card">
      <div class="hero-copy">
        <p class="eyebrow">Calm tracking for mindful nights out</p>
        <img class="app-icon" :src="icon" alt="Boozelog app icon" />
        <h1 class="hero-title">Boozelog</h1>
        <p class="hero-tagline">Drink awareness without the guesswork.</p>
        <p class="hero-body">
          Boozelog helps you monitor and maintain your estimated alcohol level so you can pace
          yourself with clarity. It is built for reflection, not for legal or safe-to-drive
          decisions.
        </p>
      </div>
      <div class="hero-glass">
        <div class="glow-orb"></div>
        <div class="hero-meter">
          <div class="meter-label">Estimated level</div>
          <div class="meter-bar">
            <span></span>
          </div>
          <div class="meter-details">
            <span>Soft feedback as you log</span>
            <span>Private by design</span>
          </div>
        </div>
      </div>
    </section>

    <section class="features">
      <div class="feature-card glass-card">
        <h2>Features that feel steady</h2>
        <ul>
          <li>Log single or double drinks quickly without breaking the flow.</li>
          <li>See how drinks add up over time with a gentle timeline.</li>
          <li>Set a personal threshold to estimate when you may return below it.</li>
          <li>Get soft visual feedback based on your estimated alcohol level.</li>
          <li>Health integration keeps your drink data private and consistent.</li>
        </ul>
      </div>
      <div class="feature-card glass-card">
        <h2>HealthKit-first storage</h2>
        <p>
          Boozelog uses Apple Health (HealthKit) to store and retrieve drink data. There is no
          local database syncing, so Health stays the source of truth across the app.
        </p>
      </div>
    </section>

    <section class="disclaimer glass-card">
      <h2>Important disclaimer</h2>
      <p>
        Not a legal or safe-to-drive indicator. Estimates vary by individual.
      </p>
      <p class="disclaimer-links">
        <router-link to="/boozelog/support">Support</router-link>
        <router-link to="/boozelog/privacy">Privacy Policy</router-link>
      </p>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import icon from '@/assets/boozelog-icon.png';

export default defineComponent({
  name: 'BoozelogView',
  data() {
    return {
      icon,
    };
  },
});
</script>

<style scoped>
.boozelog {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.hero {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  gap: 32px;
  padding: 32px 36px;
  position: relative;
  overflow: hidden;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.7rem;
  color: var(--muted-ink);
  margin: 0;
}

.hero-title {
  font-family: "Palatino", "Book Antiqua", "Georgia", serif;
  font-size: clamp(2.8rem, 4vw, 4rem);
  margin: 0;
}

.app-icon {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  box-shadow: 0 12px 24px rgba(12, 36, 51, 0.2);
}

.hero-tagline {
  font-size: 1.4rem;
  color: var(--muted-ink);
  margin: 0;
}

.hero-body {
  font-size: 1.05rem;
  line-height: 1.6;
  margin: 0;
}

.hero-actions {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.hero-glass {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.glow-orb {
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(76, 143, 230, 0.35), transparent 65%);
  top: -20px;
  right: 10px;
  animation: float 14s ease-in-out infinite;
}

.hero-meter {
  width: 100%;
  padding: 24px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 16px 30px rgba(12, 36, 51, 0.12);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.meter-label {
  font-size: 0.95rem;
  color: var(--muted-ink);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.meter-bar {
  position: relative;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  overflow: hidden;
}

.meter-bar span {
  position: absolute;
  inset: 0;
  width: 62%;
  background: linear-gradient(90deg, var(--teal), var(--accent));
  border-radius: 999px;
  animation: shimmer 6s ease-in-out infinite;
}

.meter-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: var(--muted-ink);
  font-size: 0.95rem;
}

.features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.feature-card {
  padding: 26px 28px;
}

.feature-card h2 {
  font-family: "Palatino", "Book Antiqua", "Georgia", serif;
  margin: 0 0 12px;
  font-size: 1.5rem;
}

.feature-card ul {
  margin: 0;
  padding-left: 18px;
  color: var(--muted-ink);
  line-height: 1.6;
}

.feature-card li {
  margin-bottom: 8px;
}

.feature-card p {
  margin: 0;
  color: var(--muted-ink);
  line-height: 1.6;
}

.disclaimer {
  padding: 24px 28px;
  background: linear-gradient(120deg, rgba(255, 179, 122, 0.2), rgba(255, 255, 255, 0.6));
  border-left: 4px solid var(--accent-deep);
}

.disclaimer h2 {
  margin: 0 0 8px;
  font-size: 1.2rem;
}

.disclaimer p {
  margin: 0;
  color: var(--muted-ink);
}

.disclaimer-links {
  margin-top: 12px;
  display: flex;
  gap: 16px;
}

.disclaimer-links a {
  color: var(--ink);
  text-decoration: none;
  border-bottom: 2px solid var(--teal);
  padding-bottom: 2px;
}

@keyframes shimmer {
  0% {
    width: 55%;
  }
  50% {
    width: 72%;
  }
  100% {
    width: 55%;
  }
}

@media (max-width: 900px) {
  .hero {
    grid-template-columns: 1fr;
  }
}
</style>
